// src/components/did-you-know-card.tsx
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Lightbulb, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { DidYouKnowTip } from '@/data/did-you-know-tips';

interface DidYouKnowCardProps {
  tip: DidYouKnowTip;
  isVisible: boolean;
  onDismiss: () => void;
  onShowAnother?: () => void;
}

export function DidYouKnowCard({
  tip,
  isVisible,
  onDismiss,
  onShowAnother,
}: DidYouKnowCardProps) {
  const location = useLocation();
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Delay animation to allow component to mount
      const timer = setTimeout(() => setIsAnimating(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
    }
  }, [isVisible]);

  if (!isVisible && !isAnimating) {
    return null;
  }

  const categoryColors = {
    feature: 'from-blue-500/10 to-blue-600/5 border-blue-500/20',
    optimization: 'from-green-500/10 to-green-600/5 border-green-500/20',
    workflow: 'from-purple-500/10 to-purple-600/5 border-purple-500/20',
    insight: 'from-orange-500/10 to-orange-600/5 border-orange-500/20',
    advanced: 'from-pink-500/10 to-pink-600/5 border-pink-500/20',
  };

  // Get link - use explicit link if provided, otherwise use first page from pages array
  const linkTo = tip.link || (tip.pages && tip.pages.length > 0 ? tip.pages[0] : null);
  const linkText = tip.linkText || 'Try It Now';

  // Check if user is already on this page - hide button if they are
  const isOnTipPage = tip.pages?.includes(location.pathname) || false;

  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-[90] w-full max-w-[30rem] px-6 transition-all duration-500 ease-out',
        isAnimating && isVisible
          ? 'translate-x-0 opacity-100'
          : 'translate-x-[calc(100%+2rem)] opacity-0'
      )}
    >
      <Card
        className={cn(
          'relative overflow-hidden border-2 bg-gradient-to-br shadow-2xl backdrop-blur-sm',
          categoryColors[tip.category]
        )}
      >
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent dark:from-white/5 pointer-events-none" />

        {/* Close button - absolutely positioned outside the flow */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="absolute right-1.5 top-1.5 z-10 h-7 w-7 rounded-full p-0 hover:bg-background/80"
          aria-label="Dismiss tip"
        >
          <X className="h-3.5 w-3.5" />
        </Button>

        <div className="relative p-4 pb-2">
          {/* "Did You Know?" label at top */}
          <div className="mb-2 flex items-center gap-1.5">
            <Lightbulb className="h-3.5 w-3.5 text-yellow-500" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Did You Know?
            </span>
          </div>

          {/* Header with icon and title (no badge) */}
          <div className="mb-2 flex items-start gap-2.5">
            {/* Icon */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-background/80 to-background/40 text-xl shadow-sm">
              {tip.icon || <Lightbulb className="h-5 w-5" />}
            </div>

            {/* Title only - no category badge */}
            <div className="flex-1 min-w-0 pr-5">
              <h3 className="text-sm font-bold leading-tight text-foreground">
                {tip.title}
              </h3>
            </div>
          </div>

          {/* Description */}
          <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
            {tip.description}
          </p>

          {/* Bottom row: Show Another (text link) on left, Try It Now (button) on right */}
          <div className="border-t border-border/40 pt-2 flex items-center justify-between gap-3">
            {/* Show Another - text link on left */}
            {onShowAnother && (
              <button
                onClick={onShowAnother}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <RefreshCw className="h-3 w-3" />
                <span>Show Another</span>
              </button>
            )}

            {/* Try It Now - small button on right (only if not already on this page) */}
            {linkTo && !isOnTipPage && (
              <Link to={linkTo} onClick={onDismiss}>
                <Button
                  variant="default"
                  size="sm"
                  className="group h-7 px-3 text-xs shadow-md transition-all hover:shadow-lg"
                >
                  {linkText}
                  <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Animated gradient border shimmer effect */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/20 to-transparent dark:via-white/5" />
        </div>
      </Card>
    </div>
  );
}
